<template>
  <div class="row pfol__entry">
    <div class="small-12">
      <div class="submissions__topbar">
        <Sticky
          align="right"
          :stickyTop="40"
          specifyWidth="auto"
        >
          <HKLButton
            v-if="showStartBtn"
            color="orange"
            @click="startPostFitOutClick"
          >
            {{ $t('tenantProjects.postFitout.startBtn') }}
          </HKLButton>
          <HKLButton
            v-if="curReviewerWithAccess"
            action="save"
            color="darkblue"
            @click="()=>save()"
          />
          <HKLButton
            v-if="showPreviewSend"
            action="preview_send"
            color="orange"
            @click="previewSave"
          />
          <HKLButton
            v-else-if="showPreviewPreview"
            action="preview"
            color="orange"
            @click="previewSave"
          />
          <HKLButton
            v-if="showNextReviewerBtn"
            action="nextReviewer"
            color="orange"
            @click="nextReviewerClick"
          />
        </Sticky>
      </div>
    </div>
    <div
      v-if="isTenant"
      class="small-12"
    >
      <TenantPostFitOutComment
        ref="refTenantPostFitOutComments"
      />
    </div>

    <template
      v-if="isEngineer"
    >
      <div
        v-if="showCommentsTab"
        :class="{'small-6': showCommentsTab}"
      >
        <div class="pfol-container__comment">
          <PostFitOutComments
            ref="refPostFitOutComments"
          />
        </div>
      </div>
      <div :class="[showCommentsTab ? 'small-6' : 'small-12']">
        <div class="pfol-container__guideline">
          <PostFitOutGuidelineSelector />
        </div>
      </div>
      <PostFitOutNextReviewer ref="nextReviewerModal" />
      <PostFitOutPreviewSendModal
        v-if="showPreviewSend || showPreviewPreview"
        ref="previewSendModal"
      />
      <HKLModal
        ref="msgModalForFinalInspModalRef"
        :closable="true"
        :hasCancelBtn="false"
        @onOkClick="openEditModals"
      >
        <template #title>
          <div>{{ $t('tenantProjects.postFitout.cannotStartHint_1') }}</div><br>
          <div>{{ $t('tenantProjects.postFitout.cannotStartHint_2', [$t('action.ok')]) }}</div>
        </template>
      </HKLModal>
      <Provider>
        <EditInspectionModal
          ref="editPopup"
          :afterSaved="saveSendCallback"
        />
      </Provider>
    </template>
  </div>
</template>

<script>
import { computed, provide, ref, unref, onUnmounted } from '@vue/composition-api'
import _ from 'lodash'

import Sticky from '@/controls/Sticky'
import HKLButton from '@/controls/Button/HKLButton'
import PostFitOutComments from './PostFitOutComments'
import TenantPostFitOutComment from './TenantPostFitOutComment'
import PostFitOutGuidelineSelector from './PostFitOutGuidelineSelector'
import PostFitOutNextReviewer from './PostFitOutNextReviewer'
import PostFitOutPreviewSendModal from './PostFitOutPreviewSendModal'
import { POST_FITOUT_STATUS } from './consts'
import {
  hasSendAccess,
  hasPreviewAccess,
  hasCommentTechAccess,
  hasUnderReviewPostFitout,
  curReviewerWithAccess,
  useProjectId,
  useCurrentPostFitoutId,
  useUnderReviewPostFitout,
  resetPostFitout,
} from './hook'
import { useStoreActions, useStoreGetters, useStoreState } from '@/store'
import { createPostFitoutSubmission, updatePostFitoutSubmission } from '@/api/tenantProjects/postFitout.request'
import { useRefModal } from '@/controls/Modal/modal.hook'
import HKLModal from '@/controls/Modal/HKLModal'
import { buildUpdatePostFitoutRequest } from './util'
import eventBus from '@/utils/eventBus'
import { DEPENDENCY_KEYS } from '../consts'
import { utils as formUtils } from '@/plugin/InputChange'
import EditInspectionModal from '../projectControl/Inspection/edit'
import { TYPES as inspectionTypes } from '../projectControl/Inspection/consts'
import Provider from '../projectControl/Inspection/Provider'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'

export default {
  components: {
    HKLButton,
    Sticky,
    PostFitOutPreviewSendModal,
    PostFitOutNextReviewer,
    PostFitOutGuidelineSelector,
    PostFitOutComments,
    TenantPostFitOutComment,
    EditInspectionModal,
    Provider,
    HKLModal,
  },
  setup () {
    const userStore = useStoreGetters('user', ['isTenant', 'isEngineer'])
    const state = useStoreState()
    const projectId = useProjectId()
    const showStartBtn = computed(() => {
      return unref(hasCommentTechAccess) && !unref(hasUnderReviewPostFitout)
    })

    const hasCompletedLetter = computed(() => {
      return _.get(state.value, 'postFitoutSubmission.postFitouts').some(item => item.status === POST_FITOUT_STATUS.COMPLETED)
    })
    const showNextReviewerBtn = computed(() => {
      return unref(hasCommentTechAccess) && unref(hasUnderReviewPostFitout)
    })
    const showPreviewPreview = computed(() => {
      return unref(hasUnderReviewPostFitout) && unref(hasPreviewAccess)
    })
    const showPreviewSend = computed(() => {
      return unref(hasUnderReviewPostFitout) && unref(hasSendAccess)
    })
    const showCommentsTab = computed(() => {
      return unref(hasUnderReviewPostFitout) || unref(hasCompletedLetter)
    })

    const currentPostFioutId = useCurrentPostFitoutId()

    // Methods
    const postFitoutActions = useStoreActions('postFitoutSubmission', ['getUnderReviewSubmission', 'loadProjectPostFitoutSubmissions'])

    const editModals = useRefModal()
    const { inspections } = useStoreGetters('tenantProject', ['inspections'])
    const createPostFitout = async () => {
      const finalInspection = unref(inspections).find(insp => insp.type === inspectionTypes.Final)
      if (finalInspection) {
        await createPostFitoutSubmission(projectId)
        await postFitoutActions.loadProjectPostFitoutSubmissions(projectId)
        return true
      }
      return false
    }

    const { modal: msgModalForFinalInspModalRef, open: msgModalForFinalInspModal } = useRefModal()
    const canUpdateInspection = computed(() => aclHelper.getAccess(tmsPermsTypes.project.updateInspection))
    const startPostFitOutClick = async () => {
      const createdPF = await createPostFitout()
      if (createdPF) return
      msgModalForFinalInspModal()
    }

    const saveSendCallback = async ({ close }) => {
      close()
      await createPostFitout()
    }

    const openEditModals = async () => {
      if (canUpdateInspection.value) {
        await editModals.modal.value.createNewRecord({ type: inspectionTypes.Final })
      }
    }
    onUnmounted(() => {
      resetPostFitout()
    })

    const refPostFitOutComments = ref(null)
    const underReviewPostFitout = useUnderReviewPostFitout()
    const currentSubmission = ref(unref(underReviewPostFitout))

    const save = async (isSilent) => {
      const { comments, historyList } = unref(refPostFitOutComments).getRelatedPendingComments()
      const { lock } = unref(underReviewPostFitout)
      const requestBody = buildUpdatePostFitoutRequest({ comments, lock }, {
        addHistory: historyList.addHistoryList,
        removeHistroy: historyList.removeHistoryList,
      })
      await updatePostFitoutSubmission(unref(currentPostFioutId), requestBody, isSilent)
      await postFitoutActions.loadProjectPostFitoutSubmissions(projectId)
      if (!isSilent) {
        formUtils.resetDirty()
      }

      eventBus.$emit('savedCurrentPostFiout')
    }
    provide(DEPENDENCY_KEYS.POSTFITOUT_ENTRY, { saveComments: save })
    provide(DEPENDENCY_KEYS.CURRENT_SUBMISSION, currentSubmission)

    const previewSendModal = useRefModal()
    const previewSave = async () => {
      await save(true)
      currentSubmission.value = unref(underReviewPostFitout)
      previewSendModal.open()
    }

    const nextReviewerModal = useRefModal()
    const nextReviewerClick = async () => {
      await save(true)
      nextReviewerModal.open()
    }

    return {
      hasCommentTechAccess,
      refPostFitOutComments,
      showPreviewPreview,
      showPreviewSend,
      showNextReviewerBtn,
      ...userStore,
      hasUnderReviewPostFitout,
      showCommentsTab,
      curReviewerWithAccess,
      showStartBtn,
      currentPostFioutId,
      startPostFitOutClick,
      save,
      previewSave,
      nextReviewerClick,
      nextReviewerModal: nextReviewerModal.modal,
      previewSendModal: previewSendModal.modal,
      createPostFitout,
      editPopup: editModals.modal,
      msgModalForFinalInspModalRef,
      openEditModals,
      saveSendCallback,
    }
  },
}
</script>

<style lang="scss" scoped>
.pfol__entry {
  .pfol-container {
    &__comment {
      padding-right: 10px;
    }
    &__guideline {
      padding-left: 10px;
    }
  }
}
</style>
